import React, { useEffect, useState } from 'react';
import { emailApiService, EmailAccount } from '../../services/email/emailApiService';
import {
    Plus, Trash2, Mail, CheckCircle, RefreshCw, Power, PowerOff,
    X, AlertTriangle, Clock, WifiOff, ShieldAlert,
} from 'lucide-react';
import { useToast } from '../../../../foundation/altzero_base/base/hooks/use-toast';
import { usePermissions } from '../../../../foundation/altzero_base/base/contextapi/PermissionsContext';
import { useUser } from '../../../../foundation/altzero_base/base/contextapi/UserContext';

type ConfirmAction = { type: 'disconnect' | 'remove'; accountId: string };

type AccountStatus = 'connected' | 'expiring_soon' | 'disconnected';

const getAccountStatus = (account: EmailAccount): AccountStatus => {
    if (!account.is_active) return 'disconnected';
    if (account.expires_at && account.expires_at <= Date.now() + 10 * 60 * 1000) return 'expiring_soon';
    return 'connected';
};

const STATUS_CONFIG: Record<AccountStatus, { icon: React.ReactNode; label: string; iconBg: string; badgeClass: string }> = {
    connected: {
        icon: <CheckCircle className="w-3 h-3 text-green-500" />,
        label: 'Connected',
        iconBg: 'bg-green-100 text-green-600',
        badgeClass: 'text-green-600',
    },
    expiring_soon: {
        icon: <Clock className="w-3 h-3 text-orange-500" />,
        label: 'Refreshing soon',
        iconBg: 'bg-orange-100 text-orange-600',
        badgeClass: 'text-orange-600',
    },
    disconnected: {
        icon: <WifiOff className="w-3 h-3 text-red-500" />,
        label: 'Disconnected',
        iconBg: 'bg-red-100 text-red-600',
        badgeClass: 'text-red-600',
    },
};

export const EmailAccountManager: React.FC = () => {
    const { toast } = useToast();
    const { isAdmin } = usePermissions();
    const { user } = useUser();
    const currentUserId = user?.id;

    const [accounts, setAccounts] = useState<EmailAccount[]>([]);
    const [loading, setLoading] = useState(true);
    const [pageError, setPageError] = useState<string | null>(null);
    const [actionLoading, setActionLoading] = useState<string | null>(null);
const [confirmAction, setConfirmAction] = useState<ConfirmAction | null>(null);

    /** True if the current user can manage (disconnect/remove) a given account */
    const canManage = (account: EmailAccount) => isAdmin || account.is_own === true || account.user_id === currentUserId;

    const fetchAccounts = async () => {
        try {
            setLoading(true);
            setPageError(null);
            const data = await emailApiService.getOrgAccounts();
            setAccounts(data);
        } catch (err: any) {
            setPageError('Failed to load email accounts. Please refresh the page.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchAccounts();

        const params = new URLSearchParams(window.location.search);
        if (params.get('success') === 'true') {
            window.history.replaceState({}, '', window.location.pathname);
            fetchAccounts();
            toast({ title: 'Gmail account connected', description: 'Your account is now active and ready to use.' });
        } else if (params.get('success') === 'false') {
            const msg = params.get('error');
            window.history.replaceState({}, '', window.location.pathname);
            const decoded = msg ? decodeURIComponent(msg) : null;
            const friendly = decoded?.includes('access_denied')
                ? 'You cancelled the Google sign-in. No account was connected.'
                : decoded || 'Google sign-in failed. Please try again.';
            setPageError(friendly);
        }
    }, []);

    const handleConnect = async () => {
        try {
            setActionLoading('connect');
            const { url } = await emailApiService.connectAccount();
            window.location.href = url;
        } catch (err: any) {
            setActionLoading(null);
            toast({ title: 'Could not start connection', description: err.message, variant: 'destructive' });
        }
    };

    const handleReconnect = async (id: string) => {
        try {
            setActionLoading(id);
            const { url } = await emailApiService.reconnectAccount(id);
            window.location.href = url;
        } catch (err: any) {
            setActionLoading(null);
            toast({ title: 'Could not start reconnection', description: err.message, variant: 'destructive' });
        }
    };

    const handleRefreshToken = async (id: string, email: string) => {
        try {
            setActionLoading(`refresh-${id}`);
            const result = await emailApiService.refreshToken(id);
            if (result.success) {
                setAccounts(prev => prev.map(a => a.id === id ? { ...a, is_active: true } : a));
                toast({ title: 'Token refreshed', description: `${email} is now connected again.` });
            } else {
                const isRevoked = result.error?.toLowerCase().includes('invalid_grant') ||
                    result.error?.toLowerCase().includes('revoked');
                if (isRevoked) {
                    toast({
                        title: 'Google access revoked',
                        description: 'This account\'s permission was removed from Google. Please reconnect via Google sign-in.',
                        variant: 'destructive',
                    });
                } else {
                    toast({
                        title: 'Refresh failed',
                        description: result.error || 'Could not refresh the token. Try reconnecting instead.',
                        variant: 'destructive',
                    });
                }
            }
        } catch (err: any) {
            toast({ title: 'Refresh failed', description: err.message, variant: 'destructive' });
        } finally {
            setActionLoading(null);
        }
    };

    const handleDisconnect = async (id: string) => {
        try {
            setActionLoading(id);
            setConfirmAction(null);
            await emailApiService.disconnectAccount(id);
            setAccounts(prev => prev.map(a => a.id === id ? { ...a, is_active: false } : a));
            toast({ title: 'Account disconnected', description: 'You can reconnect it at any time.' });
        } catch (err: any) {
            toast({ title: 'Disconnect failed', description: err.message, variant: 'destructive' });
        } finally {
            setActionLoading(null);
        }
    };

    const handleRemove = async (id: string) => {
        try {
            setActionLoading(id);
            setConfirmAction(null);
            await emailApiService.removeAccount(id);
            setAccounts(prev => prev.filter(a => a.id !== id));
            toast({ title: 'Account removed', description: 'The email account has been permanently deleted.' });
        } catch (err: any) {
            toast({ title: 'Remove failed', description: err.message, variant: 'destructive' });
        } finally {
            setActionLoading(null);
        }
    };



    return (
        <div className="bg-white rounded-lg shadow p-6">
            <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-semibold flex items-center gap-2">
                    <Mail className="w-5 h-5" />
                    Connected Email Accounts
                </h2>
                <button
                    onClick={handleConnect}
                    disabled={actionLoading === 'connect'}
                    className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    {actionLoading === 'connect' ? (
                        <RefreshCw className="w-4 h-4 animate-spin" />
                    ) : (
                        <Plus className="w-4 h-4" />
                    )}
                    Connect Gmail
                </button>
            </div>

            {/* Page-level error */}
            {pageError && (
                <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-md mb-4 flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                    <span className="flex-1 text-sm">{pageError}</span>
                    <button onClick={() => setPageError(null)} className="text-red-400 hover:text-red-600">
                        <X className="w-4 h-4" />
                    </button>
                </div>
            )}

            {loading ? (
                <div className="flex justify-center p-8">
                    <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
                </div>
            ) : accounts.length === 0 ? (
                <div className="text-center py-8 text-gray-500 border-2 border-dashed border-gray-200 rounded-lg">
                    <Mail className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    <p className="font-medium">No email accounts connected</p>
                    <p className="text-sm mt-1">Connect a Gmail account to send and receive tender emails.</p>
                </div>
            ) : (
                <div className="space-y-3">
                    {accounts.map(account => {
                        const status = getAccountStatus(account);
                        const cfg = STATUS_CONFIG[status];
                        const isActionBusy = actionLoading === account.id || actionLoading === `refresh-${account.id}`;
                        const isPendingConfirm = confirmAction?.accountId === account.id;
                        const manageable = canManage(account);
                        const isOwn = account.is_own === true || account.user_id === currentUserId;

                        return (
                            <div key={account.id} className="border rounded-lg overflow-hidden">
                                <div className="flex items-center justify-between p-4 hover:bg-gray-50 transition-colors">
                                    {/* Left: icon + info */}
                                    <div className="flex items-center gap-3">
                                        <div className={`p-2 rounded-full ${cfg.iconBg}`}>
                                            <Mail className="w-5 h-5" />
                                        </div>
                                        <div>
                                            <div className="flex items-center gap-2">
                                                <p className="font-medium text-sm">{account.email}</p>
                                                {!isOwn && (
                                                    <span className="text-xs bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded">
                                                        {account.owner_name ?? 'Team member'}
                                                    </span>
                                                )}
                                                {isOwn && (
                                                    <span className="text-xs bg-blue-50 text-blue-600 px-1.5 py-0.5 rounded font-medium">
                                                        You
                                                    </span>
                                                )}
                                            </div>
                                            <div className="flex items-center gap-2 text-xs text-gray-500 mt-0.5 flex-wrap">
                                                <span className={`flex items-center gap-1 font-medium ${cfg.badgeClass}`}>
                                                    {cfg.icon}
                                                    {cfg.label}
                                                </span>
                                                <span>•</span>
                                                <span>Added {new Date(account.created_at).toLocaleDateString()}</span>
                                                {account.updated_at && account.updated_at !== account.created_at && (
                                                    <>
                                                        <span>•</span>
                                                        <span>Last synced {new Date(account.updated_at).toLocaleDateString()}</span>
                                                    </>
                                                )}
                                                {account.is_default && (
                                                    <>
                                                        <span>•</span>
                                                        <span className="text-blue-600 font-medium">Default</span>
                                                    </>
                                                )}
                                            </div>
                                            {status === 'disconnected' && manageable && (
                                                <p className="text-xs text-red-500 mt-1">
                                                    This account cannot send emails. Try refreshing or reconnect via Google.
                                                </p>
                                            )}
                                            {status === 'disconnected' && !manageable && (
                                                <p className="text-xs text-red-500 mt-1">
                                                    Disconnected — only the account owner or an admin can reconnect this.
                                                </p>
                                            )}
                                            {status === 'expiring_soon' && (
                                                <p className="text-xs text-orange-500 mt-1">
                                                    Token refreshing automatically — no action needed.
                                                </p>
                                            )}
                                        </div>
                                    </div>

                                    {/* Right: actions — shown only if user can manage this account */}
                                    <div className="flex items-center gap-1.5">
                                        {!manageable ? (
                                            <span className="flex items-center gap-1 text-xs text-gray-400 px-2">
                                                <ShieldAlert className="w-3.5 h-3.5" />
                                                View only
                                            </span>
                                        ) : status === 'disconnected' ? (
                                            <button
                                                onClick={() => handleReconnect(account.id)}
                                                disabled={isActionBusy}
                                                className="flex items-center gap-1 px-3 py-1.5 text-xs bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                                title="Re-authorize via Google sign-in"
                                            >
                                                {actionLoading === account.id ? (
                                                    <RefreshCw className="w-3 h-3 animate-spin" />
                                                ) : (
                                                    <Power className="w-3 h-3" />
                                                )}
                                                Reconnect
                                            </button>
                                        ) : (
                                            <>
                                                <button
                                                    onClick={() => setConfirmAction({ type: 'disconnect', accountId: account.id })}
                                                    disabled={isActionBusy}
                                                    className="flex items-center gap-1 px-2.5 py-1.5 text-xs text-gray-600 hover:text-yellow-600 hover:bg-yellow-50 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                                    title="Temporarily disconnect"
                                                >
                                                    <PowerOff className="w-3 h-3" />
                                                    Disconnect
                                                </button>
                                            </>
                                        )}

                                        {manageable && (
                                            <button
                                                onClick={() => setConfirmAction({ type: 'remove', accountId: account.id })}
                                                disabled={isActionBusy}
                                                className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                                title="Permanently delete account"
                                            >
                                                <Trash2 className="w-3.5 h-3.5" />
                                            </button>
                                        )}
                                    </div>
                                </div>

                                {/* Inline confirmation banner */}
                                {isPendingConfirm && (
                                    <div className={`px-4 py-3 border-t flex items-center justify-between gap-3 ${
                                        confirmAction?.type === 'remove' ? 'bg-red-50 border-red-100' : 'bg-yellow-50 border-yellow-100'
                                    }`}>
                                        <div className="flex items-center gap-2">
                                            <AlertTriangle className={`w-4 h-4 ${confirmAction?.type === 'remove' ? 'text-red-500' : 'text-yellow-500'}`} />
                                            <span className="text-sm font-medium">
                                                {confirmAction?.type === 'remove'
                                                    ? 'Permanently delete this account? This cannot be undone.'
                                                    : 'Disconnect this account? You can reconnect it any time.'}
                                            </span>
                                        </div>
                                        <div className="flex items-center gap-2 shrink-0">
                                            <button
                                                onClick={() => setConfirmAction(null)}
                                                className="px-3 py-1 text-xs text-gray-600 bg-white border rounded-md hover:bg-gray-50 transition-colors"
                                            >
                                                Cancel
                                            </button>
                                            <button
                                                onClick={() =>
                                                    confirmAction?.type === 'remove'
                                                        ? handleRemove(account.id)
                                                        : handleDisconnect(account.id)
                                                }
                                                disabled={isActionBusy}
                                                className={`px-3 py-1 text-xs text-white rounded-md transition-colors disabled:opacity-50 ${
                                                    confirmAction?.type === 'remove'
                                                        ? 'bg-red-600 hover:bg-red-700'
                                                        : 'bg-yellow-600 hover:bg-yellow-700'
                                                }`}
                                            >
                                                {isActionBusy ? (
                                                    <RefreshCw className="w-3 h-3 animate-spin" />
                                                ) : confirmAction?.type === 'remove' ? 'Delete' : 'Disconnect'}
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};
